// src/pages/PurchaseLeads.tsx
import React, { useState } from "react";
import { collection, addDoc } from "firebase/firestore";
import { db } from "../firebase/firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Sun } from "lucide-react";
import { z } from "zod";
import { useNavigate } from 'react-router-dom';
import ProgressBar from "../components/ProgressBar";
import FormAside from "../components/FormAside";
import { useRecaptcha } from "../hooks/useRecaptcha";
import "./styles/PurchaseLeads.css";

const formSchema = z.object({
  companyName: z.string().min(1, "Company Name is required").max(100),
  companyType: z.string().min(1, "Company Type is required"),
  website: z.string().min(1, "Website is required").max(200),
  contactName: z.string().min(1, "Contact Name is required").max(100),
  email: z.string().email("Invalid Email").max(100),
  phone: z
    .string()
    .min(1, "Phone is required")
    .max(15)
    .transform((val) => val.replace(/\D/g, "")),
  state: z.string().min(1, "State is required"),
  leadTypes: z.array(z.string()).min(1, "Select at least one lead type"),
  monthlyBudget: z.string().min(1, "Monthly Budget is required"),
  leadVolume: z.string().min(1, "Lead Volume is required"),
  contactConsent: z.literal(true, {
    errorMap: () => ({ message: "You must consent to be contacted" }),
  }),
});

type FormData = z.infer<typeof formSchema>;

const allStates = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];

const leadTypes = [
  "Residential Solar",
  "Commercial Solar",
  "Solar + Storage",
  "EV Charging",
  "Energy Efficiency",
  "Other",
];

const functions = getFunctions();
const verifyRecaptcha = httpsCallable<
  { token: string; action: string },
  { success: boolean; score?: number }
>(functions, "verifyRecaptcha");

export default function PurchaseLeads() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    isReady: recaptchaReady,
    execute: executeRecaptcha,
    reset: resetRecaptcha,
  } = useRecaptcha(import.meta.env.VITE_RECAPTCHA_SITE_KEY);

  const [formData, setFormData] = useState<FormData>({
    companyName: "",
    companyType: "",
    website: "",
    contactName: "",
    email: "",
    phone: "",
    state: "",
    leadTypes: [],
    monthlyBudget: "",
    leadVolume: "",
    contactConsent: true,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>(
    {}
  );
  const totalSteps = 4;

  const validateStep = (step: number): boolean => {
    let fields: (keyof FormData)[] = [];
    if (step === 1) fields = ["companyName", "companyType", "website"];
    if (step === 2) fields = ["contactName", "email", "phone", "state"];
    if (step === 3) fields = ["leadTypes", "monthlyBudget", "leadVolume"];
    if (step === 4) fields = ["contactConsent"];
    try {
      const stepSchema = formSchema.pick(
        Object.fromEntries(fields.map((f) => [f, true])) as {
          [K in keyof FormData]?: true;
        }
      );
      stepSchema.parse(formData);
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof z.ZodError) {
        const newErrors: Partial<Record<keyof FormData, string>> = {};
        err.errors.forEach((e) => {
          if (e.path[0]) newErrors[e.path[0] as keyof FormData] = e.message;
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((s) => Math.min(s + 1, totalSteps));
    }
  };

  const prevStep = () => setCurrentStep((s) => Math.max(s - 1, 1));

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = e.target instanceof HTMLInputElement ? e.target.checked : undefined;
    if (type === "checkbox") {
      if (name === "leadTypes") {
        setFormData((prev) => ({
          ...prev,
          leadTypes: checked
            ? [...prev.leadTypes, value]
            : prev.leadTypes.filter((v) => v !== value),
        }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: checked }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!recaptchaReady) throw new Error("reCAPTCHA not ready");
      const token = await executeRecaptcha("submit");
      if (!token) throw new Error("Token missing");
      const result = await verifyRecaptcha({ token, action: "submit" });
      if (!result.data.success) throw new Error("reCAPTCHA failed");
      await addDoc(collection(db, "leadBuyers"), {
        ...formData,
        timestamp: new Date(),
        status: "pending",
      });
      setIsSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      resetRecaptcha();
    } finally {
      setLoading(false);
    }
  };

  if (isSubmitted)
    return (
      <div className="solar-form-card">
        <div className="solar-form-card-header text-center">
          <h2 className="solar-form-card-title success">Thank You!</h2>
          <p className="solar-form-card-description">
            Your lead buyer application has been submitted. We will contact you
            soon.
          </p>
        </div>
        <div className="solar-form-card-content center">
          <Sun className="solar-form-icon" />
        </div>
        <div className="solar-form-card-footer center">
          <button
            onClick={() => setIsSubmitted(false)}
            className="solar-form-reset-btn"
          >
            Submit Another Application
          </button>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="form-layout">
        <div className="solar-form-card">
          <div className="solar-form-card-header">
            <h2 className="solar-form-card-title">Lead Buyer Application</h2>
            <p className="solar-form-card-description">
              Please complete the form to become a lead buyer.
            </p>
          </div>
          <div className="solar-form-card-content">
            <form className="solar-form">
              <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />

              {/* Step 1: Company Info */}
              {currentStep === 1 && (
                <>
                  <label>Company Name</label>
                  <input
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                  />
                  {errors.companyName && (
                    <div className="error-message">{errors.companyName}</div>
                  )}
                  <label>Company Type</label>
                  <input
                    name="companyType"
                    value={formData.companyType}
                    onChange={handleChange}
                  />
                  {errors.companyType && (
                    <div className="error-message">{errors.companyType}</div>
                  )}
                  <label>Website</label>
                  <input
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                  />
                  {errors.website && (
                    <div className="error-message">{errors.website}</div>
                  )}
                </>
              )}

              {/* Step 2: Contact Info */}
              {currentStep === 2 && (
                <>
                  <label>Contact Name</label>
                  <input
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleChange}
                  />
                  {errors.contactName && (
                    <div className="error-message">{errors.contactName}</div>
                  )}
                  <label>Email</label>
                  <input
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                  {errors.email && (
                    <div className="error-message">{errors.email}</div>
                  )}
                  <label>Phone</label>
                  <input
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                  {errors.phone && (
                    <div className="error-message">{errors.phone}</div>
                  )}
                  <label>State</label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                  >
                    <option value="">Select a state</option>
                    {allStates.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                  {errors.state && (
                    <div className="error-message">{errors.state}</div>
                  )}
                </>
              )}

              {/* Step 3: Lead Requirements */}
              {currentStep === 3 && (
                <>
                  <label>Lead Types</label>
                  {leadTypes.map((type) => (
                    <div key={type}>
                      <label>
                        <input
                          type="checkbox"
                          name="leadTypes"
                          value={type}
                          checked={formData.leadTypes.includes(type)}
                          onChange={handleChange}
                        />
                        {type}
                      </label>
                    </div>
                  ))}
                  {errors.leadTypes && (
                    <div className="error-message">{errors.leadTypes}</div>
                  )}
                  <label>Monthly Budget</label>
                  <input
                    name="monthlyBudget"
                    value={formData.monthlyBudget}
                    onChange={handleChange}
                  />
                  {errors.monthlyBudget && (
                    <div className="error-message">{errors.monthlyBudget}</div>
                  )}
                  <label>Lead Volume</label>
                  <input
                    name="leadVolume"
                    value={formData.leadVolume}
                    onChange={handleChange}
                  />
                  {errors.leadVolume && (
                    <div className="error-message">{errors.leadVolume}</div>
                  )}
                </>
              )}

              {/* Step 4: Payment & Terms */}
              {currentStep === 4 && (
                <>
                  <label>
                    <input
                      type="checkbox"
                      name="contactConsent"
                      checked={formData.contactConsent}
                      onChange={handleChange}
                    />
                    I consent to be contacted by Energy Guru regarding my lead purchase inquiry
                  </label>
                  {errors.contactConsent && (
                    <div className="error-message">{errors.contactConsent}</div>
                  )}
                </>
              )}

              <div className="solar-form-navigation">
                {currentStep > 1 && (
                  <button
                    type="button"
                    onClick={prevStep}
                    className="solar-form-prev-btn"
                  >
                    Previous
                  </button>
                )}
                {currentStep < totalSteps ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="solar-form-next-btn"
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="solar-form-submit"
                    disabled={loading}
                  >
                    {loading ? "Submitting..." : "Submit Application"}
                  </button>
                )}
              </div>

              {error && <div className="solar-form-error">{error}</div>}

              <div className="recaptcha-notice">
                <small>Protected by reCAPTCHA Enterprise</small>
                <p>
                  This site is protected by reCAPTCHA and the Google{" "}
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://policies.google.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Service
                  </a>{" "}
                  apply.
                </p>
              </div>
            </form>
          </div>
        </div>
        <FormAside
          heading="Why buy leads with us"
          points={[
            "Real, consent-based homeowner leads",
            "Filter by state & lead type",
            "No long-term contracts",
          ]}
          steps={[
            {
              title: "Tell us your target",
              text: "Your industry, states, budget, and volume.",
            },
            {
              title: "We review your application",
              text: "Our team reaches out within 24 hours.",
            },
            {
              title: "Start receiving leads",
              text: "Fresh, scored leads matched to your criteria.",
            },
          ]}
          footnote="Questions before you commit? We'll walk you through everything — no obligation."
        />
        </div>
      </div>
    </div>
  );
}
